import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import TemplateViewComponent from '../commonComponents/TemplateView';
import { url } from '../../util/config';

// ── Type icon/color config ───────────────────────────────────────────────────
const TYPE_CONFIG = {
    Image:    { icon: 'bi-image',        color: '#7c3aed', bg: '#f5f3ff' },
    Video:    { icon: 'bi-play-circle',  color: '#0891b2', bg: '#ecfeff' },
    Document: { icon: 'bi-file-earmark', color: '#d97706', bg: '#fffbeb' },
    Text:     { icon: 'bi-type',         color: '#1565c0', bg: '#eff6ff' },
    Carousel: { icon: 'bi-images',       color: '#16a34a', bg: '#f0fdf4' },
};

const getTypeConfig = (type = '') => TYPE_CONFIG[type] || { icon: 'bi-layout-text-window', color: '#6b7280', bg: '#f9fafb' };

const CATEGORY_COLORS = {
    Marketing:      { bg: '#fef3c7', color: '#d97706' },
    Utility:        { bg: '#eff6ff', color: '#1565c0' },
    Authentication: { bg: '#f0fdf4', color: '#16a34a' },
};

const getCategoryColor = (cat = '') => CATEGORY_COLORS[cat] || { bg: '#f3f4f6', color: '#6b7280' };

// ── Template Card ────────────────────────────────────────────────────────────
const TemplateCard = ({ template, onPreview }) => {
    const typeCfg = getTypeConfig(template.template_type);
    const catColor = getCategoryColor(template.category);
    const [imgError, setImgError] = useState(false);

    return (
        <div style={cardStyles.card}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'; }}>

            {/* Preview area */}
            <div style={cardStyles.preview}>
                {template.template_type === 'Image' && template.template_media_url && !imgError ? (
                    <img
                        src={template.template_media_url}
                        alt="Template"
                        style={cardStyles.previewImg}
                        onError={() => setImgError(true)}
                    />
                ) : template.template_type === 'Video' && template.template_media_url ? (
                    <div style={cardStyles.videoThumb}>
                        <i className="bi bi-play-circle-fill" style={{ fontSize: 36, color: 'white', opacity: 0.9 }}></i>
                    </div>
                ) : (
                    <div style={{ ...cardStyles.previewPlaceholder, background: typeCfg.bg }}>
                        <i className={`bi ${typeCfg.icon}`} style={{ fontSize: 28, color: typeCfg.color }}></i>
                        {template.template_message_body && (
                            <p style={cardStyles.bodySnippet}>
                                {template.template_message_body.slice(0, 60)}{template.template_message_body.length > 60 ? '…' : ''}
                            </p>
                        )}
                    </div>
                )}

                {/* Eye button overlay */}
                <button style={cardStyles.eyeBtn} onClick={() => onPreview(template)} title="View template">
                    <i className="bi bi-eye-fill"></i>
                </button>
            </div>

            {/* Card body */}
            <div style={cardStyles.body}>
                <div style={cardStyles.titleRow}>
                    <span style={cardStyles.templateName} title={template.template_name}>
                        {template.template_name}
                    </span>
                    <span style={{ ...cardStyles.statusDot, background: template.status ? '#16a34a' : '#dc2626' }}
                        title={template.status ? 'Active' : 'Inactive'}></span>
                </div>

                <div style={cardStyles.metaRow}>
                    <span style={{ ...cardStyles.catBadge, background: catColor.bg, color: catColor.color }}>
                        {template.category}
                    </span>
                    <span style={{ ...cardStyles.typeBadge, background: typeCfg.bg, color: typeCfg.color }}>
                        <i className={`bi ${typeCfg.icon}`} style={{ marginRight: 4, fontSize: 11 }}></i>
                        {template.template_type || 'Text'}
                    </span>
                </div>

                <div style={cardStyles.footer}>
                    <span style={cardStyles.date}>
                        <i className="bi bi-calendar3" style={{ marginRight: 4 }}></i>
                        {template.createdAt}
                    </span>
                    {template.button_json?.length > 0 && (
                        <span style={cardStyles.btnCount}>
                            <i className="bi bi-grid-1x2" style={{ marginRight: 4 }}></i>
                            {template.button_json.length} btn{template.button_json.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const cardStyles = {
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', transition: 'transform 0.15s, box-shadow 0.15s', display: 'flex', flexDirection: 'column' },
    preview: { position: 'relative', height: '140px', overflow: 'hidden', background: '#f8fafc' },
    previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
    videoThumb: { width: '100%', height: '100%', background: 'linear-gradient(135deg, #1565c0, #0d2b5e)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    previewPlaceholder: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0 12px' },
    bodySnippet: { margin: 0, fontSize: '11px', color: '#6b7280', textAlign: 'center', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
    eyeBtn: { position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '6px', color: 'white', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, backdropFilter: 'blur(4px)' },
    body: { padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
    titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
    templateName: { fontSize: '13px', fontWeight: '700', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
    statusDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
    metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    catBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
    typeBadge: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' },
    date: { fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center' },
    btnCount: { fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center' },
};

// ── Preview Modal ────────────────────────────────────────────────────────────
const PreviewModal = ({ template, onClose }) => {
    const typeCfg = getTypeConfig(template.template_type);
    const catColor = getCategoryColor(template.category);

    return (
        <div style={modalStyles.overlay} onClick={onClose}>
            <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={modalStyles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ ...modalStyles.iconWrap, background: typeCfg.bg }}>
                            <i className={`bi ${typeCfg.icon}`} style={{ fontSize: 16, color: typeCfg.color }}></i>
                        </div>
                        <div>
                            <h6 style={modalStyles.title}>{template.template_name}</h6>
                            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                <span style={{ ...cardStyles.catBadge, ...catColor }}>{template.category}</span>
                                <span style={{ ...cardStyles.typeBadge, background: typeCfg.bg, color: typeCfg.color }}>
                                    <i className={`bi ${typeCfg.icon}`} style={{ marginRight: 4, fontSize: 11 }}></i>
                                    {template.template_type || 'Text'}
                                </span>
                                <span style={{ ...cardStyles.catBadge, background: template.status ? '#dcfce7' : '#fee2e2', color: template.status ? '#16a34a' : '#dc2626' }}>
                                    {template.status ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button style={modalStyles.closeBtn} onClick={onClose}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Body — two column: info + WA preview */}
                <div style={modalStyles.body}>
                    {/* Left — template details */}
                    <div style={modalStyles.leftCol}>
                        {template.template_header_text && (
                            <div style={modalStyles.infoGroup}>
                                <label style={modalStyles.infoLabel}>Header</label>
                                <p style={modalStyles.infoValue}>{template.template_header_text}</p>
                            </div>
                        )}
                        {template.template_message_body && (
                            <div style={modalStyles.infoGroup}>
                                <label style={modalStyles.infoLabel}>Message Body</label>
                                <p style={{ ...modalStyles.infoValue, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{template.template_message_body}</p>
                            </div>
                        )}
                        {template.template_footer_text && (
                            <div style={modalStyles.infoGroup}>
                                <label style={modalStyles.infoLabel}>Footer</label>
                                <p style={modalStyles.infoValue}>{template.template_footer_text}</p>
                            </div>
                        )}
                        {template.template_media_url && (
                            <div style={modalStyles.infoGroup}>
                                <label style={modalStyles.infoLabel}>Media URL</label>
                                <a href={template.template_media_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1565c0', wordBreak: 'break-all' }}>
                                    {template.template_media_url}
                                </a>
                            </div>
                        )}
                        {template.button_json?.length > 0 && (
                            <div style={modalStyles.infoGroup}>
                                <label style={modalStyles.infoLabel}>Buttons ({template.button_json.length})</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {template.button_json.map((btn, i) => (
                                        <div key={i} style={modalStyles.btnRow}>
                                            <span style={modalStyles.btnType}>{btn.type?.replace(/_/g, ' ')}</span>
                                            <span style={{ fontSize: 13, color: '#374151' }}>{btn.button || btn.content || btn.site_url}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div style={modalStyles.infoGroup}>
                            <label style={modalStyles.infoLabel}>Created</label>
                            <p style={modalStyles.infoValue}>{template.createdAt}</p>
                        </div>
                    </div>

                    {/* Right — WA preview */}
                    <div style={modalStyles.rightCol}>
                        <p style={modalStyles.previewLabel}>
                            <i className="bi bi-whatsapp" style={{ marginRight: 6, color: '#25d366' }}></i>
                            WhatsApp Preview
                        </p>
                        <div style={modalStyles.waPhone}>
                            <div style={modalStyles.waHeader}>
                                <i className="bi bi-arrow-left" style={{ color: 'white', marginRight: 8 }}></i>
                                <span style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>{template.template_name}</span>
                            </div>
                            <div style={modalStyles.waBody}>
                                <div style={modalStyles.waBubble}>
                                    <TemplateViewComponent template={template} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const modalStyles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 },
    box: { background: 'white', borderRadius: '14px', width: '100%', maxWidth: '860px', maxHeight: '90vh', boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    iconWrap: { width: '42px', height: '42px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0, flexShrink: 0 },
    body: { display: 'flex', flex: 1, overflow: 'hidden' },
    leftCol: { flex: 1, padding: '20px', overflowY: 'auto', borderRight: '1px solid #f3f4f6' },
    rightCol: { width: '300px', flexShrink: 0, padding: '20px', background: '#f0f2f5', overflowY: 'auto' },
    infoGroup: { marginBottom: 16 },
    infoLabel: { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 },
    infoValue: { margin: 0, fontSize: '14px', color: '#111827' },
    btnRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' },
    btnType: { fontSize: '11px', fontWeight: '600', background: '#eff6ff', color: '#1565c0', padding: '2px 6px', borderRadius: 4 },
    previewLabel: { fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
    waPhone: { borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
    waHeader: { background: '#075e54', padding: '12px 16px', display: 'flex', alignItems: 'center' },
    waBody: { background: '#ece5dd', padding: '12px', minHeight: 160 },
    waBubble: { background: 'white', borderRadius: '0 8px 8px 8px', padding: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
};

// ── Main Component ───────────────────────────────────────────────────────────
const TemplateComponent = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15; // cards per page

    const fetchTemplates = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/template`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (!response.ok) throw new Error('Failed to fetch templates');
            const data = await response.json();
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setError('Failed to load templates. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    useEffect(() => { setCurrentPage(1); }, [categoryFilter, statusFilter, searchTerm]);

    const filteredTemplates = templates.filter(t => {
        const matchCat = categoryFilter === '' || t.category === categoryFilter;
        const matchStatus = statusFilter === '' || t.status === (statusFilter === 'true');
        const matchSearch = searchTerm === '' || t.template_name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchCat && matchStatus && matchSearch;
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTemplates = filteredTemplates.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);

    const handleReset = () => { setCategoryFilter(''); setStatusFilter(''); setSearchTerm(''); };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>WhatsApp Templates</h5>
                        <p style={styles.pageSubtitle}>Manage your message templates</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={() => navigate('/createTemplate')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Add New Template
                    </button>
                </div>

                {/* Filters */}
                <div style={styles.filterCard}>
                    <div style={styles.filterRow}>
                        <div style={styles.searchWrapper}>
                            <i className="bi bi-search" style={styles.searchIcon}></i>
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Category</label>
                            <select style={styles.filterSelect} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                <option value="">All Categories</option>
                                <option value="Marketing">Marketing</option>
                                <option value="Utility">Utility</option>
                                <option value="Authentication">Authentication</option>
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Status</label>
                            <select style={styles.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                        {(categoryFilter || statusFilter || searchTerm) && (
                            <button style={styles.btnSecondary} onClick={handleReset}>
                                <i className="bi bi-x" style={{ marginRight: 4 }}></i>Reset
                            </button>
                        )}
                        <span style={styles.resultCount}>{filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}</span>
                    </div>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div style={styles.centerState}>
                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                        <p style={styles.stateText}>Loading templates…</p>
                    </div>
                ) : error ? (
                    <div style={styles.centerState}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: 36, color: '#ef4444' }}></i>
                        <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                        <button style={styles.btnPrimary} onClick={fetchTemplates}>Retry</button>
                    </div>
                ) : filteredTemplates.length === 0 ? (
                    <div style={styles.centerState}>
                        <i className="bi bi-layout-text-window-reverse" style={{ fontSize: 40, color: '#d1d5db' }}></i>
                        <p style={styles.stateText}>
                            {searchTerm || categoryFilter || statusFilter
                                ? 'No templates match your filters.'
                                : 'No templates yet. Create your first template.'}
                        </p>
                        {!searchTerm && !categoryFilter && !statusFilter && (
                            <button style={styles.btnPrimary} onClick={() => navigate('/createTemplate')}>Add New Template</button>
                        )}
                    </div>
                ) : (
                    <>
                        <div style={styles.cardGrid}>
                            {paginatedTemplates.map(template => (
                                <TemplateCard
                                    key={template._id}
                                    template={template}
                                    onPreview={setSelectedTemplate}
                                />
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Preview Modal */}
            {selectedTemplate && (
                <PreviewModal template={selectedTemplate} onClose={() => setSelectedTemplate(null)} />
            )}
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterRow: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' },
    searchWrapper: { position: 'relative', flex: 1, minWidth: 200 },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' },
    searchInput: { width: '100%', padding: '8px 12px 8px 36px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    filterLabel: { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white', minWidth: 140 },
    resultCount: { fontSize: '13px', color: '#6b7280', alignSelf: 'center', whiteSpace: 'nowrap' },
    cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '80px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default TemplateComponent;
